import { useEffect, useMemo, useRef } from 'react'
import { NavLink, Outlet } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import {
  Bell,
  Briefcase,
  Calendar,
  CalendarRange,
  LayoutDashboard,
  LogOut,
  Newspaper,
  PanelLeftClose,
  PanelLeftOpen,
  Percent,
  Settings2,
} from 'lucide-react'
import { fetchForecastViews, fetchForecastYears, fetchMyAlerts } from '@/api/client'
import { isFinanceUser, isOwnerUser, isViewerUser, type UserRole } from '@/api/types'
import { useAuth } from '@/auth/useAuth'
import { useAppChrome } from '@/context/AppChromeContext'
import { AppVersion } from '@/components/AppVersion'
import { IconButton } from '@/components/IconButton'
import { useToast } from '@/components/toast-context'
import { useSidebarCollapsed } from '@/hooks/useSidebarCollapsed'
import { QUARTER_OPTIONS, type ForecastQuarter } from '@/lib/quarter'

const ROLE_SUBTITLE: Partial<Record<UserRole, string>> = {
  owner: 'Opportunity owner',
  finance: 'Finance',
  finance_admin: 'Finance admin',
  admin: 'Admin',
  system_admin: 'System admin',
  viewer: 'Viewer',
  reviewer: 'Reviewer',
}

function navClass({ isActive }: { isActive: boolean }) {
  return `layout__nav-link${isActive ? ' active' : ''}`
}

export function Layout() {
  const { user, signOut } = useAuth()
  const { year, viewCode, quarter, setYear, setViewCode, setQuarter } = useAppChrome()
  const { collapsed, toggle } = useSidebarCollapsed()
  const toast = useToast()
  const invalidViewToastRef = useRef<string | null>(null)
  const finance = isFinanceUser(user)
  const viewer = isViewerUser(user)
  const owner = isOwnerUser(user)
  const showForecastSelectors = finance || !viewer

  const yearsQuery = useQuery({
    queryKey: ['config', 'years'],
    queryFn: () => fetchForecastYears(),
  })

  const viewsQuery = useQuery({
    queryKey: ['config', 'views'],
    queryFn: fetchForecastViews,
  })

  const alertsQuery = useQuery({
    queryKey: ['alerts', 'mine'],
    queryFn: fetchMyAlerts,
    enabled: Boolean(user),
  })

  const openYears = yearsQuery.data?.filter((y) => y.is_open ?? y.open) ?? []
  const views = useMemo(() => viewsQuery.data ?? [], [viewsQuery.data])

  useEffect(() => {
    if (!views.length) return
    if (views.some((view) => view.code === viewCode)) {
      invalidViewToastRef.current = null
      return
    }
    const fallback =
      views.find((view) => view.is_default)?.code ?? views[0]?.code ?? 'probability_weighted'
    if (fallback === viewCode) return
    if (invalidViewToastRef.current !== viewCode) {
      invalidViewToastRef.current = viewCode
      toast.push(`Unknown view "${viewCode}" — switched to default.`, 'info')
    }
    setViewCode(fallback)
  }, [views, viewCode, setViewCode, toast])

  const alertCount = alertsQuery.data?.length ?? 0
  const roleKey = (user?.roles?.[0] ?? user?.role) as UserRole | undefined
  const roleSubtitle =
    (roleKey && ROLE_SUBTITLE[roleKey]) ||
    (finance ? 'Finance admin' : owner ? 'Opportunity owner' : 'User')
  const opportunitiesLabel = finance ? 'All opportunities' : 'My opportunities'

  return (
    <div className={`layout${collapsed ? ' layout--sidebar-collapsed' : ''}`}>
      <aside className="layout__sidebar" aria-label="App">
        <div className="layout__sidebar-top">
          <div className="layout__brand" title="ARIES vForecast · Neurealm">
            <div className="layout__brand-row">
              <img
                className="layout__brand-mark"
                src="/brand/neurealm-mark.png"
                alt=""
                width={36}
                height={36}
              />
              {!collapsed && (
                <div className="layout__brand-lockup">
                  <span className="layout__logo">ARIES</span>
                  <span className="layout__tagline">vForecast</span>
                </div>
              )}
            </div>
            {!collapsed && <AppVersion />}
          </div>
          <IconButton
            label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            variant="ghost"
            onClick={toggle}
          >
            {collapsed ? (
              <PanelLeftOpen size={18} strokeWidth={2} />
            ) : (
              <PanelLeftClose size={18} strokeWidth={2} />
            )}
          </IconButton>
        </div>

        <nav className="layout__nav" aria-label="Main">
          <NavLink
            to="/opportunities"
            className={navClass}
            title={opportunitiesLabel}
            aria-label={opportunitiesLabel}
          >
            <Briefcase size={18} strokeWidth={2} aria-hidden />
            <span className="layout__nav-text">{opportunitiesLabel}</span>
          </NavLink>
          {showForecastSelectors && (
            <NavLink
              to="/forecast"
              className={navClass}
              title="Monthly forecast"
              aria-label="Monthly forecast"
            >
              <CalendarRange size={18} strokeWidth={2} aria-hidden />
              <span className="layout__nav-text">Monthly</span>
            </NavLink>
          )}
          {finance && (
            <NavLink
              to="/console"
              className={navClass}
              title="Finance console"
              aria-label="Finance console"
            >
              <LayoutDashboard size={18} strokeWidth={2} aria-hidden />
              <span className="layout__nav-text">Finance console</span>
            </NavLink>
          )}
          {(viewer || finance) && (
            <NavLink
              to="/published"
              className={navClass}
              title="Published forecast"
              aria-label="Published forecast"
            >
              <Newspaper size={18} strokeWidth={2} aria-hidden />
              <span className="layout__nav-text">Published forecast</span>
            </NavLink>
          )}
          {finance && (
            <NavLink to="/admin" className={navClass} title="Admin" aria-label="Admin">
              <Settings2 size={18} strokeWidth={2} aria-hidden />
              <span className="layout__nav-text">Admin</span>
            </NavLink>
          )}
          <NavLink
            to="/alerts"
            className={({ isActive }) => `${navClass({ isActive })} layout__nav-link--badge`}
            title={alertCount > 0 ? `Alerts (${alertCount})` : 'Alerts'}
            aria-label={alertCount > 0 ? `Alerts (${alertCount})` : 'Alerts'}
          >
            <Bell size={18} strokeWidth={2} aria-hidden />
            <span className="layout__nav-text">
              Alerts{alertCount > 0 ? ` (${alertCount})` : ''}
            </span>
            {alertCount > 0 && <span className="layout__badge">{alertCount}</span>}
          </NavLink>
        </nav>

        <div className="layout__sidebar-spacer" />

        {showForecastSelectors && (
          <div className="layout__selectors">
            <label className="layout__selector" title={`Forecast year: ${year}`}>
              <Calendar size={16} strokeWidth={2} aria-hidden />
              <select
                className="field__input field__input--compact layout__select"
                value={year}
                onChange={(event) => setYear(Number(event.target.value))}
                aria-label="Forecast year"
              >
                {(openYears.length ? openYears : [{ year, open: true }]).map((y) => (
                  <option key={y.year} value={y.year}>
                    {y.year}
                  </option>
                ))}
              </select>
            </label>
            <label
              className="layout__selector"
              title={`Quarter: ${QUARTER_OPTIONS.find((q) => q.value === quarter)?.label ?? 'Full year'}`}
            >
              <CalendarRange size={16} strokeWidth={2} aria-hidden />
              <select
                className="field__input field__input--compact layout__select"
                value={quarter === 'all' ? 'all' : String(quarter)}
                onChange={(event) => {
                  const raw = event.target.value
                  const next: ForecastQuarter =
                    raw === '1' || raw === '2' || raw === '3' || raw === '4'
                      ? (Number(raw) as 1 | 2 | 3 | 4)
                      : 'all'
                  setQuarter(next)
                }}
                aria-label="Forecast quarter"
              >
                {QUARTER_OPTIONS.map((option) => (
                  <option key={String(option.value)} value={String(option.value)}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
            <label
              className="layout__selector"
              title={`Forecast view: ${views.find((v) => v.code === viewCode)?.name ?? viewCode}`}
            >
              <Percent size={16} strokeWidth={2} aria-hidden />
              <select
                className="field__input field__input--compact layout__select"
                value={viewCode}
                onChange={(event) => setViewCode(event.target.value)}
                aria-label="Forecast view"
              >
                {(views.length
                  ? views
                  : [
                      {
                        id: 'default',
                        code: viewCode,
                        name: 'Probability weighted',
                        apply_weighting: true,
                      },
                    ]
                ).map((view) => (
                  <option key={view.id} value={view.code}>
                    {view.name}
                  </option>
                ))}
              </select>
            </label>
          </div>
        )}

        {user && (
          <div className="layout__user" title={`${user.full_name} · ${roleSubtitle}`}>
            <div className="layout__user-meta">
              <span className="layout__user-name">{user.full_name}</span>
              <span className="layout__user-role">{roleSubtitle}</span>
            </div>
            <IconButton label="Sign out" variant="ghost" onClick={() => void signOut()}>
              <LogOut size={16} strokeWidth={2} />
            </IconButton>
          </div>
        )}
      </aside>

      <main className="layout__main">
        <Outlet />
      </main>
    </div>
  )
}
